import React, { useEffect, useState, useCallback } from 'react';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, ReferenceLine, Brush, AreaChart, Area
} from 'recharts';

const timeOptions = [
  { label: '1H', value: '1h', days: '1', interval: 'minute' },
  { label: '1D', value: '1d', days: '1', interval: 'hourly' },
  { label: '1W', value: '7d', days: '7', interval: 'hourly' },
  { label: '1M', value: '30d', days: '30', interval: 'daily' },
  { label: '1Y', value: '1y', days: '365', interval: 'daily' },
  { label: 'All', value: 'all', days: 'max', interval: 'daily' },
];

const CACHE_DURATION = 30 * 60 * 1000;

const formatPrice = (price) => {
  if (!price) return '--';
  if (price >= 1000) return price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  if (price >= 1) return price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 });
  if (price >= 0.01) return price.toLocaleString(undefined, { minimumFractionDigits: 4, maximumFractionDigits: 5 });
  if (price >= 0.0001) return price.toLocaleString(undefined, { minimumFractionDigits: 5, maximumFractionDigits: 6 });
  return price.toLocaleString(undefined, { minimumFractionDigits: 6, maximumFractionDigits: 8 });
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-gray-800 p-2 border border-gray-700 rounded shadow-md">
        <p className="text-sm text-gray-300">{label}</p>
        <p className="text-sm font-bold text-white">
          ${formatPrice(payload[0].value)}
        </p>
      </div>
    );
  }
  return null;
};

const CoinChart = ({ coinId, coinName }) => {
  const [data, setData] = useState([]);
  const [selectedOption, setSelectedOption] = useState(timeOptions[1]);
  const [priceChange, setPriceChange] = useState(null);
  const [currentPrice, setCurrentPrice] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const formatTimestamp = useCallback((timestamp) => {
    const date = new Date(timestamp);
    switch (selectedOption.value) {
      case '1h': return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
      case '1d': return `${date.getHours().toString().padStart(2, '0')}:00`;
      case '7d': return new Intl.DateTimeFormat('en-US', { weekday: 'short' }).format(date);
      case '30d': return `${date.getDate()}/${date.getMonth() + 1}`;
      case '1y':
      case 'all': return new Intl.DateTimeFormat('en-US', { month: 'short', year: '2-digit' }).format(date);
      default: return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(date);
    }
  }, [selectedOption.value]);

  const getCacheKey = useCallback((coinId, optionValue) => `coinChart_${coinId}_${optionValue}`, []);

  const processData = useCallback((rawData) => {
    const chartData = rawData
      .filter(([_, price]) => price !== undefined && price !== null)
      .map(([timestamp, price], i) => ({
        time: formatTimestamp(timestamp),
        timestamp,
        price: parseFloat(price.toFixed(6)),
        volume: Math.random() * 1000 // Placeholder for volume, ideally replace with real volume data
      }));
    const first = chartData[0]?.price;
    const last = chartData[chartData.length - 1]?.price;
    const change = first && last ? (((last - first) / first) * 100).toFixed(2) : null;
    setData(chartData);
    setCurrentPrice(last);
    setPriceChange(change);
    return { chartData, last, change };
  }, [formatTimestamp]);

  const getValidCache = useCallback((cacheKey) => {
    const cached = localStorage.getItem(cacheKey);
    if (!cached) return null;
    try {
      const parsed = JSON.parse(cached);
      if (Date.now() - parsed.timestamp < CACHE_DURATION) return parsed;
    } catch (e) {
      localStorage.removeItem(cacheKey);
    }
    return null;
  }, []);

  const saveToCache = useCallback((cacheKey, chartData, currentPrice, priceChange) => {
    localStorage.setItem(cacheKey, JSON.stringify({ timestamp: Date.now(), chartData, currentPrice, priceChange }));
  }, []);

  const fetchChartData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    const cacheKey = getCacheKey(coinId, selectedOption.value);
    const validCache = getValidCache(cacheKey);
    if (validCache) {
      processData(validCache.chartData.map(d => [d.timestamp, d.price]));
      setIsLoading(false);
      return;
    }
    try {
      const url = `https://api.coingecko.com/api/v3/coins/${coinId}/market_chart?vs_currency=usd&days=${selectedOption.days}&interval=${selectedOption.interval}`;
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Failed: ${response.status}`);
      const json = await response.json();
      const prices = json.prices;
      const processed = processData(prices);
      saveToCache(cacheKey, prices.map(([timestamp, price]) => ({ timestamp, price })), processed.last, processed.change);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [coinId, selectedOption, getCacheKey, getValidCache, processData, saveToCache]);

  useEffect(() => {
    fetchChartData();
  }, [fetchChartData]);

  const isPositive = priceChange !== null && parseFloat(priceChange) >= 0;
  const chartColor = isPositive ? '#16c784' : '#ea3943';

  return (
    <div className="p-4 rounded-xl shadow-md border-gray-800">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-300 mb-1">
            {coinName || coinId.charAt(0).toUpperCase() + coinId.slice(1).replace(/-/g, ' ')}
          </h3>
          {isLoading ? (
            <div className="animate-pulse">
              <div className="h-8 w-32 bg-gray-700 rounded"></div>
              <div className="h-4 w-16 mt-2 bg-gray-700 rounded"></div>
            </div>
          ) : (
            <>
              <h2 className="text-2xl font-bold text-gray-100">
                ${formatPrice(currentPrice)}
              </h2>
              {priceChange !== null && (
                <p className={`text-sm font-medium ${isPositive ? 'text-green-500' : 'text-red-500'}`}>
                  {isPositive ? '▲' : '▼'} {Math.abs(priceChange)}%
                </p>
              )}
            </>
          )}
        </div>
      </div>

      <div className="flex justify-end gap-1 mb-4 flex-wrap">
        {timeOptions.map((opt) => (
          <button
            key={opt.label}
            onClick={() => setSelectedOption(opt)}
            className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
              selectedOption.value === opt.value ? 'bg-blue-600 text-white' : 'bg-gray-800 hover:bg-gray-700 text-gray-300'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {error ? (
        <div className="h-64 flex items-center justify-center text-yellow-500">
          <p>{error}</p>
        </div>
      ) : isLoading ? (
        <div className="h-64 flex items-center justify-center">
          <div className="animate-spin h-8 w-8 border-4 border-blue-500 rounded-full border-t-transparent"></div>
        </div>
      ) : (
        <div className="h-64 md:h-80">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
              <XAxis dataKey="time" tick={{ fontSize: 12, fill: '#9CA3AF' }} tickLine={{ stroke: '#6B7280' }} axisLine={{ stroke: '#6B7280' }} />
              <YAxis domain={['auto', 'auto']} tickFormatter={val => `$${val}`} tick={{ fontSize: 12, fill: '#9CA3AF' }} tickLine={{ stroke: '#6B7280' }} axisLine={{ stroke: '#6B7280' }} width={60} />
              <Tooltip content={<CustomTooltip />} />
              <ReferenceLine y={data[0]?.price} stroke="#6B7280" strokeDasharray="3 3" />
              <Line type="monotone" dataKey="price" stroke={chartColor} strokeWidth={2} dot={false} activeDot={{ r: 6, fill: chartColor, stroke: '#1F2937', strokeWidth: 2 }} animationDuration={800} isAnimationActive />
              <Area type="monotone" dataKey="volume" fill="#1F2937" stroke="#6B7280" opacity={0.3} yAxisId={0} />
              <Brush dataKey="time" height={20} stroke="#374151" travellerWidth={10} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

export default CoinChart;

